import React, { useState, useEffect } from 'react';
import './PromptInput.css';

const PromptInput = ({ inputWords, setInputWords, currentInput, setCurrentInput }) => {
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [totalChars, setTotalChars] = useState(0);
  const MAX_CHARS = 600;

  useEffect(() => {
    // Calculate total characters including current input
    const totalLength = inputWords.join(' ').length + currentInput.length;
    setTotalChars(totalLength);
  }, [inputWords, currentInput]);

  const handleInputKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === 'Tab' || e.key === ',') && currentInput.trim()) {
      e.preventDefault();
      const newWord =
        e.key === ',' ? currentInput.trim().replace(/,$/, '') : currentInput.trim();

      // Check if adding the new word would exceed the character limit
      const newTotalChars =
        inputWords.join(' ').length + newWord.length + (inputWords.length > 0 ? 1 : 0);

      if (newTotalChars <= MAX_CHARS) {
        if (e.key === ',' && newWord) {
          addWord(newWord);
        } else if (e.key !== ',') {
          addWord(newWord);
        }
      }
    } else if (e.key === 'Backspace' && currentInput === '' && inputWords.length > 0) {
      e.preventDefault();
      const newWords = [...inputWords];
      newWords.pop();
      setInputWords(newWords);
    }
  };

  /**
   * UPDATED handlePaste:
   *  - Removes the logic that only adds intermediate parts to inputWords
   *    and puts the last part into currentInput.
   *  - Splits on ',' and adds each trimmed chunk as a tag (if it fits).
   */
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');

    // If there's at least one comma, split into multiple tags
    if (pastedText.includes(',')) {
      const parts = pastedText.split(',');

      // We'll collect new words here:
      let newWords = [...inputWords];

      // Add each comma-separated part as a new word, if it fits
      parts.forEach((rawPart) => {
        const trimmedPart = rawPart.trim();
        if (trimmedPart) {
          const newTotalChars =
            newWords.join(' ').length + trimmedPart.length + (newWords.length > 0 ? 1 : 0);
          if (newTotalChars <= MAX_CHARS) {
            newWords.push(trimmedPart);
          }
        }
      });

      // Update inputWords and clear currentInput
      setInputWords(newWords);
      setCurrentInput('');
    } else {
      // No commas: treat the entire pasted text as current input
      const newTotalChars =
        inputWords.join(' ').length + pastedText.length + (inputWords.length > 0 ? 1 : 0);
      if (newTotalChars <= MAX_CHARS) {
        setCurrentInput(pastedText);
      }
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    // Allow typing if within character limit
    const newTotalChars =
      inputWords.join(' ').length + value.length + (inputWords.length > 0 ? 1 : 0);
    if (newTotalChars <= MAX_CHARS) {
      setCurrentInput(value);
    }
  };

  const addWord = (word) => {
    if (word) {
      const newTotalChars =
        inputWords.join(' ').length + word.length + (inputWords.length > 0 ? 1 : 0);
      if (newTotalChars <= MAX_CHARS) {
        setInputWords([...inputWords, word]);
        setCurrentInput('');
      }
    }
  };

  const handleBlur = () => {
    if (currentInput.trim()) {
      addWord(currentInput.trim());
    }
  };

  const removeWord = (indexToRemove) => {
    setInputWords(inputWords.filter((_, index) => index !== indexToRemove));
  };

  const startEditing = (index, word) => {
    setEditingIndex(index);
    setEditingValue(word);
  };

  const handleEditKeyDown = (e, index) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      finishEditing(index);
    } else if (e.key === 'Escape') {
      setEditingIndex(null);
      setEditingValue('');
    }
  };

  const handleEditChange = (e) => {
    const value = e.target.value;
    // Calculate new total length if this edit were applied
    const currentWords = [...inputWords];
    currentWords[editingIndex] = value;
    const newTotalChars = currentWords.join(' ').length;

    if (newTotalChars <= MAX_CHARS) {
      setEditingValue(value);
    }
  };

  const finishEditing = (index) => {
    if (editingValue.trim()) {
      const newWords = [...inputWords];
      const oldWord = newWords[index];
      newWords[index] = editingValue.trim();

      // Check if the edit would exceed character limit
      const newTotalChars = newWords.join(' ').length;
      if (newTotalChars <= MAX_CHARS) {
        setInputWords(newWords);
      } else {
        // Revert if it would exceed limit
        newWords[index] = oldWord;
        setInputWords(newWords);
      }
    }
    setEditingIndex(null);
    setEditingValue('');
  };

  return (
    <div className="input-tags-container">
      <div className="input-tags">
        {inputWords.map((word, index) => (
          <span key={index} className="input-tag">
            {editingIndex === index ? (
              <input
                type="text"
                value={editingValue}
                onChange={handleEditChange}
                onKeyDown={(e) => handleEditKeyDown(e, index)}
                onBlur={() => finishEditing(index)}
                className="tag-edit-input"
                autoFocus
              />
            ) : (
              <>
                <span onDoubleClick={() => startEditing(index, word)}>
                  {word}
                </span>
                <button onClick={() => removeWord(index)} className="remove-tag">
                  ×
                </button>
              </>
            )}
          </span>
        ))}
        <input
          type="text"
          value={currentInput}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          onBlur={handleBlur}
          onPaste={handlePaste}
          placeholder={
            inputWords.length === 0
              ? 'Enter words for your prompt (press Tab, Enter, or comma to add)...'
              : ''
          }
          className="tag-input"
        />
      </div>
      <div className="input-info">
        <span>{inputWords.length} words</span>
        <span>
          {totalChars}/{MAX_CHARS} characters
        </span>
      </div>
    </div>
  );
};

export default PromptInput;
